import React from 'react'
import { InteractiveContentRenderer } from '../interactive'

const OscillationLessonDemo: React.FC = () => {
  // Load nội dung từ JSON (simplified version)
  const lessonContent = [
    {
      type: 'text',
      content: 'Chào mừng bạn đến với bài học "Dao động điều hòa - Phương trình dao động điều hòa" lớp 11!'
    },
    {
      type: 'heading',
      level: 2,
      content: '1. Khái niệm dao động điều hòa'
    },
    {
      type: 'text',
      content: 'Dao động điều hòa là dao động trong đó li độ của vật biến thiên theo thời gian theo quy luật hàm cosin (hoặc sin).'
    },
    {
      type: 'interactive_oscillation',
      title: 'Mô phỏng dao động điều hòa',
      description: 'Quan sát chuyển động dao động điều hòa. Chú ý đường hình sin bên dưới thể hiện sự biến thiên của li độ theo thời gian. Thay đổi các tham số để thấy sự khác biệt.'
    },
    {
      type: 'text',
      content: 'Từ mô phỏng trên, bạn có thể thấy:'
    },
    {
      type: 'list',
      style: 'bullet',
      items: [
        'Vật dao động qua lại quanh vị trí cân bằng (O)',
        'Li độ x biến thiên theo thời gian theo đường hình sin/cosin',
        'Vận tốc (mũi tên xanh) lớn nhất tại vị trí cân bằng, bằng 0 tại vị trí biên',
        'Gia tốc (mũi tên đỏ) luôn hướng về vị trí cân bằng, lớn nhất tại vị trí biên'
      ]
    },
    {
      type: 'interactive_question',
      question: 'Quan sát mô phỏng trên, khi nào vật có vận tốc lớn nhất?',
      options: [
        'Tại vị trí biên (x = ±A)',
        'Tại vị trí cân bằng (x = 0)',
        'Tại vị trí x = A/2',
        'Vận tốc luôn không đổi'
      ],
      correctAnswer: 1,
      explanation: 'Vật có vận tốc lớn nhất khi đi qua vị trí cân bằng (x = 0). Tại đây, toàn bộ năng lượng được chuyển thành động năng.',
      hint: 'Quan sát độ dài mũi tên xanh (vận tốc) tại các vị trí khác nhau',
      difficulty: 'easy'
    },
    {
      type: 'heading',
      level: 2,
      content: '2. Phương trình dao động điều hòa'
    },
    {
      type: 'text',
      content: 'Phương trình dao động điều hòa có dạng tổng quát:'
    },
    {
      type: 'formula',
      content: 'x = A\\cos(\\omega t + \\varphi)',
      explanation: 'Trong đó x là li độ (độ lệch khỏi vị trí cân bằng) tại thời điểm t'
    },
    {
      type: 'interactive_formula',
      title: 'Tính toán với phương trình dao động',
      formula: 'x = A\\cos(\\omega t + \\varphi)',
      variables: [
        {
          symbol: 'A',
          name: 'Biên độ',
          unit: 'cm',
          min: 1,
          max: 20,
          default: 10,
          step: 0.5
        },
        {
          symbol: 'ω',
          name: 'Tần số góc',
          unit: 'rad/s',
          min: 0.5,
          max: 5,
          default: 2,
          step: 0.1
        },
        {
          symbol: 't',
          name: 'Thời gian',
          unit: 's',
          min: 0,
          max: 10,
          default: 0,
          step: 0.1
        },
        {
          symbol: 'φ',
          name: 'Pha ban đầu',
          unit: 'rad',
          min: 0,
          max: 6.28,
          default: 0,
          step: 0.1
        }
      ],
      resultFormula: 'A * cos(ω * t + φ)',
      resultUnit: 'cm',
      description: 'Thay đổi các tham số để xem li độ thay đổi như thế nào',
      insights: [
        'Khi t = 0, li độ x₀ = A·cos(φ)',
        'Li độ dao động trong khoảng từ -A đến +A',
        'Chu kỳ dao động T = 2π/ω',
        'Khi ω tăng, dao động nhanh hơn (chu kỳ giảm)'
      ]
    },
    {
      type: 'interactive_exploration',
      title: 'Khám phá các thành phần của phương trình',
      description: 'Tìm hiểu ý nghĩa vật lý của từng thành phần trong phương trình dao động điều hòa',
      steps: [
        {
          id: 'step1',
          title: 'Li độ x',
          content: 'Li độ x là độ lệch của vật khỏi vị trí cân bằng tại thời điểm t. Li độ có thể dương hoặc âm tùy theo chiều chọn.',
          reveal: 'Đơn vị: mét (m). Li độ dương khi vật ở một phía vị trí cân bằng, âm khi ở phía đối diện. |x| ≤ A luôn đúng.'
        },
        {
          id: 'step2',
          title: 'Biên độ dao động A',
          content: 'Biên độ A là giá trị cực đại của li độ. Nó cho biết vật dao động xa nhất bao nhiêu so với vị trí cân bằng.',
          reveal: 'A > 0 luôn đúng. Đơn vị: mét (m). Biên độ phụ thuộc vào năng lượng dao động: năng lượng càng lớn, biên độ càng lớn.'
        },
        {
          id: 'step3', 
          title: 'Tần số góc ω',
          content: 'Tần số góc ω đặc trưng cho tốc độ dao động. Nó liên hệ với chu kỳ T và tần số f.',
          reveal: 'ω = 2π/T = 2πf, đơn vị: rad/s. Tần số góc chỉ phụ thuộc vào tính chất của hệ dao động, không phụ thuộc vào biên độ.'
        },
        {
          id: 'step4',
          title: 'Pha ban đầu φ',
          content: 'Pha ban đầu φ xác định vị trí và chiều chuyển động của vật tại thời điểm t = 0.',
          reveal: 'Đơn vị: radian (rad). φ phụ thuộc vào điều kiện ban đầu: x₀ = A cos φ và v₀ = -Aω sin φ.'
        }
      ]
    },
    {
      type: 'interactive_question',
      question: 'Một vật dao động với phương trình x = 6cos(4t + π/4) cm. Biên độ dao động là:',
      options: [
        'A = 4 cm',
        'A = 6 cm',
        'A = π/4 cm',
        'A = 4π cm'
      ],
      correctAnswer: 1,
      explanation: 'So sánh với phương trình tổng quát x = A cos(ωt + φ), ta có A = 6 cm.',
      hint: 'Biên độ A là hệ số đứng trước hàm cos',
      difficulty: 'easy'
    }
  ]

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          📚 Dao động điều hòa - Phương trình dao động điều hòa
        </h1>
        <p className="text-lg text-gray-600">
          Lớp 11 - Chương 1 - Bài 1
        </p>
        <div className="mt-4 flex items-center justify-center space-x-4 text-sm text-gray-500">
          <span>⏱️ 90 phút</span>
          <span>📊 Trung bình</span>
          <span>🎯 Nội dung tương tác</span>
        </div>
      </div>

      <InteractiveContentRenderer 
        content={lessonContent}
        onProgress={(progress) => {
          console.log('Lesson progress:', progress)
        }}
      />
    </div>
  )
}

export default OscillationLessonDemo